// Vues façon X : une publication compte 1 vue quand sa barre d'actions est visible ≥ 1 s,
// une seule fois par appareil (mémoire locale) ; l'auteur ne compte jamais ses propres vues.
import { useEffect } from "react";
import { api } from "@/lib/api";
import { emitPostPatch } from "@/lib/postSync";

export const VIEWED_KEY = "yalola.viewed_posts";
const MAX_REMEMBERED = 800;
export const VIEW_DWELL_MS = 1000;
const inflight = new Set(); // même publication rendue 2× (fil + théâtre) → un seul appel

const readViewed = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(VIEWED_KEY) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

export const alreadyViewed = (id) => readViewed().includes(id);

export const markViewed = (id) => {
  try {
    const arr = readViewed().filter((x) => x !== id);
    arr.push(id);
    localStorage.setItem(VIEWED_KEY, JSON.stringify(arr.slice(-MAX_REMEMBERED)));
  } catch { /* stockage indisponible → on compte quand même */ }
};

export const shouldTrackView = (post, user) =>
  !!post?.id && !post.is_ad && !(user && user.id === post.author_id) && !alreadyViewed(post.id);

const isVisibleEnough = (entry) => {
  if (!entry.isIntersecting) return false;
  const rootH = entry.rootBounds?.height || (typeof window !== "undefined" ? window.innerHeight : 0);
  return entry.intersectionRatio >= 0.5 || (rootH > 0 && entry.intersectionRect.height >= rootH * 0.5);
};

export const sendView = (postId) => {
  if (inflight.has(postId)) return;
  inflight.add(postId);
  markViewed(postId);
  api.post(`/posts/${postId}/view`)
    .then(({ data }) => { if (typeof data?.views_count === "number") emitPostPatch(postId, { views_count: data.views_count }); })
    .catch(() => {})
    .finally(() => inflight.delete(postId));
};

export function usePostViewTracker(ref, post, user) {
  const postId = post?.id;
  const authorId = post?.author_id;
  const isAd = !!post?.is_ad;
  const userId = user?.id;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    if (!shouldTrackView({ id: postId, author_id: authorId, is_ad: isAd }, userId ? { id: userId } : null)) return undefined;
    let timer = null;
    const io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      if (isVisibleEnough(e)) {
        if (timer) return;
        timer = setTimeout(() => { timer = null; io.disconnect(); sendView(postId); }, VIEW_DWELL_MS);
      } else if (timer) { clearTimeout(timer); timer = null; }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [ref, postId, authorId, isAd, userId]);
}
